import { useState } from 'react';
import {
  Search,
  Filter,
  Edit,
  XCircle,
  User,
  Shield
} from 'lucide-react';
import AdminHeader from '@/react-app/components/AdminHeader';
import { useAdminStats } from "./hooks/useAdminStats";
import { useDebounce } from './hooks/useDebounce';
import { useAuthStore } from '@/shared/stores/authStore';
import type { IUserFinancialData } from '@/shared/types';
import { useTheme } from '@/react-app/contexts/ThemeContext';
import { api } from '@/shared/api/endpoints';
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";


const formatDate = (dateString: string) => {
  if (!dateString) return 'Not provided';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export default function UserManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<IUserFinancialData | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const { user: authStoreUser, isAuthenticated } = useAuthStore();
  const [kycStatus, setVerifyStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const queryClient = useQueryClient();



  const debouncedSearch = useDebounce(searchTerm, 500);

  const { data: stats, isLoading } = useAdminStats(debouncedSearch, currentPage, filterStatus);



  const verifyKYC = [
    { value: '', label: 'Select' },
    { value: 'Verified', label: 'Accept' },
    { value: 'Rejected', label: 'Reject' },
  ];

  const { mutate: updateKYC } = useMutation({
    mutationFn: async ({ kycStatus, userId }: { kycStatus: string, userId: string }) => {
      return await api.admin.verifyKYC(kycStatus, userId);
    },
    onSuccess: async (verificationResponse) => {
      if (verificationResponse.success) {

        toast.success("KYC updated successfully 🎉");
        queryClient.invalidateQueries({ queryKey: ["adminStats"] });
        setLoading(false);
      } else {
        toast.error("kyc verification failed");
        setLoading(false);

      }
    },
    onError: (error: any) => {
      console.error("kyc verification error:", error);
      toast.error(error.message || "kyc verification");
      setLoading(false);
    },
  });

  const handleUserAction = async (userId: string) => {
    setLoading(true);
    try {
      if (kycStatus == "") return;
      updateKYC({ kycStatus, userId });
    } catch (error) {
      console.error(`Error ${error} user:`, error);
    }
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (isActive: string) => {
    return isActive == "active"
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const getPackageBadge = (packageName: string) => {
    const colors = {
      'none': 'bg-gray-100 text-gray-800',
      'saver': 'bg-blue-100 text-blue-800',
      'saver_plus': 'bg-purple-100 text-purple-800',
      'saver_pro': 'bg-green-100 text-green-800',
      'saver_premium': 'bg-orange-100 text-orange-800',
      'saver_elite': 'bg-red-100 text-red-800',
    };

    return colors[packageName as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  // const totalPages = Math.ceil(totalUsers / usersPerPage);
  if (!isAuthenticated || authStoreUser?.role == "user") {
    window.location.href = '/login';
    return null;
  }


  if (isLoading) {
    return (
      <div className="min-h-screen dark:bg-gray-900 bg-gray-50">
        <AdminHeader />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage and monitor platform users</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email, name, or referral code..."
                value={searchTerm}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSearchTerm(e.target.value);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => {
                  setCurrentPage(1);
                  setFilterStatus(e.target.value);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="">All Users</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-between items-center text-sm text-gray-600">
            <span>Showing {stats?.pagination.count} of {stats?.totalUsers} users</span>
            <span>Page {currentPage} of {stats?.pagination.totalPages}</span>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">User</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Balance</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Package</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Savings Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Joined</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">KYC Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Kyc Doc</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats?.financial.map((item) => (
                  <tr key={item.user._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          {item.user.role == "admin" ? (
                            <Shield className="h-5 w-5 text-red-600" />
                          ) : (
                            <User className="h-5 w-5 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 flex items-center space-x-2">
                            <span>{item.user.firstName + " " + item.user.lastName || 'Unnamed User'}</span>
                            {item.user.role == "admin" && (
                              <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                                Admin
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{item.user.email}</div>
                          <div className="text-xs text-gray-400">ID: {item.user.referralCode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {formatCurrency(item.wallet?.balance ?? 0)}
                        </div>
                        <div className="text-gray-500">
                          Deposited: {formatCurrency(item.wallet?.totalDeposits ?? 0)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPackageBadge(item.packages[0]?.packageId?.name)}`}>
                        {item.packages.length === 0 ? 'No Package' : item.packages[0]?.packageId?.name?.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.packages[0]?.status)}`}>
                        {item.packages[0]?.status == "active" ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatDate(item.user.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {item.user.kycDocument?.status}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <a
                          href={item.user.kycDocument?.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          View Document
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedUser(item);
                            setShowUserModal(true);
                          }}
                          className="text-red-600 hover:text-red-700 p-1 rounded transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>

                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {stats?.pagination?.totalPages && stats.pagination.totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <span>
                  Page {stats?.pagination?.page} of {stats?.pagination?.totalPages}
                </span>

                <div className="flex space-x-2">
                  {Array.from({ length: Math.min(5, stats?.pagination?.totalPages ?? 0) }, (_, i) => {
                    const page = i + Math.max(1, currentPage - 2);
                    if (page > (stats?.pagination?.totalPages ?? 0)) return null;

                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${currentPage === page
                          ? "bg-red-600 text-white"
                          : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(stats?.pagination?.totalPages ?? 0, currentPage + 1))}
                  disabled={currentPage === stats?.pagination?.totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}

        </div>

        {/* User Details Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">User Details</h3>
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* User Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={selectedUser.user.firstName + " " + selectedUser.user.firstName || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={selectedUser.user.email}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                </div>

                {/* Financial Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Balance
                    </label>
                    <input
                      type="text"
                      value={formatCurrency(selectedUser.wallet?.balance ?? 0)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Deposited
                    </label>
                    <input
                      type="text"
                      value={formatCurrency(selectedUser.wallet?.totalDeposits ?? 0)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Referral Code
                  </label>
                  <input
                    type="text"
                    value={selectedUser.user.referralCode}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
                <div>
                  <select
                    value={kycStatus}
                    onChange={(e) => setVerifyStatus(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                      ? 'bg-gray-600 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  >
                    {verifyKYC.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Actions */}
                <div className="flex space-x-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleUserAction(selectedUser.user._id)}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${selectedUser.user.kycDocument?.status == "Verified"
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                  >
                    {
                      loading ? "Processing..." :
                        selectedUser.user?.kycDocument?.status == "Verified" ? 'Reject KYC' : 'Accept KYC'
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
